import { useEffect, useState } from "react";
import { useAuthedApi } from "@/api/useAuthedApi";
import { useCurrentUser } from "@/auth/RequireAuth";
import type { UserProfile } from "@/api/profileApi";
import type { AuthUser } from "@/auth/useAuth";

/** Best short name for greetings: preferred name > first name > first token of
 *  the full name. Returns null when none is set — callers then fall back to a
 *  name-less greeting rather than showing an email. */
export function resolveName(
  profile: Pick<UserProfile, "preferred_name" | "first_name"> | null,
  user: Pick<AuthUser, "name"> | null,
): string | null {
  const preferred = profile?.preferred_name?.trim();
  if (preferred) return preferred;
  const first = profile?.first_name?.trim();
  if (first) return first;
  const fromFull = user?.name?.trim().split(/\s+/)[0];
  if (fromFull) return fromFull;
  return null;
}

// Module-level cache: the profile name is stable for a session, so every
// consumer shares one fetch (mirrors useCompany).
let cached: Pick<UserProfile, "preferred_name" | "first_name"> | null = null;
let inflight: Promise<void> | null = null;

/** Test hook — clears the module cache between cases. */
export function _resetPreferredNameCache(): void {
  cached = null;
  inflight = null;
}

export interface PreferredName {
  /** Resolved name, or null when the user has none set. */
  name: string | null;
  /** False until the profile fetch has settled (success OR failure). Lets the
   *  greeting hold until the name is known so it renders once, not twice. */
  ready: boolean;
}

export function usePreferredName(): PreferredName {
  const { profile } = useAuthedApi();
  const user = useCurrentUser();
  const [ready, setReady] = useState<boolean>(cached !== null);

  useEffect(() => {
    if (cached) {
      setReady(true);
      return;
    }
    inflight ??= profile.get().then(
      (p) => {
        cached = { preferred_name: p.preferred_name, first_name: p.first_name };
      },
      () => {
        // Soft-fail: settle anyway so the greeting can render (name-less or
        // from the auth user). Clear inflight so the next mount retries.
        inflight = null;
      },
    );
    let cancelled = false;
    void inflight.then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [profile]);

  return { name: resolveName(cached, user), ready };
}
